import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Bell, Mail, Palette, AlignLeft, X, Plus, Trash2, Users, Check, Eye, AlertCircle } from 'lucide-react';
import { eventsApi, type Event, type CreateEventData, type EventType, type EventPriority, type TeamMember, type AssignmentStatus } from '../../services/events';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface EventFormData {
  title: string;
  type: EventType;
  priority: EventPriority;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  all_day: boolean;
  location: string;
  description: string;
  email_notification_enabled: boolean;
  email_recipients: string;
  reminder_enabled: boolean;
  reminder_days: number;
  color: string;
  notes: string;
  assigned_user_ids: number[];
}

const defaultFormData: EventFormData = {
  title: '',
  type: 'event',
  priority: 'medium',
  start_date: '',
  start_time: '',
  end_date: '',
  end_time: '',
  all_day: false,
  location: '',
  description: '',
  email_notification_enabled: false,
  email_recipients: '',
  reminder_enabled: false,
  reminder_days: 3,
  color: '#3B82F6',
  notes: '',
  assigned_user_ids: [],
};

const colorOptions = [
  { value: '#3B82F6', label: 'Blue' },
  { value: '#EF4444', label: 'Red' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Orange' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#6366F1', label: 'Indigo' },
];

const Planner: React.FC = () => {
  useAuth();
  const [formData, setFormData] = useState<EventFormData>(defaultFormData);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchTeamMembers();
    fetchUnreadCount();
  }, []);

  const fetchEvents = async () => {
    try {
      const data = await eventsApi.getEvents({ include_assigned: true });
      setEvents(data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const members = await eventsApi.getTeamMembers();
      setTeamMembers(members);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const data = await eventsApi.getUnreadCount();
      setUnreadCount(data.total);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleMemberToggle = (memberId: number) => {
    setFormData(prev => ({
      ...prev,
      assigned_user_ids: prev.assigned_user_ids.includes(memberId)
        ? prev.assigned_user_ids.filter(id => id !== memberId)
        : [...prev.assigned_user_ids, memberId],
    }));
  };

  const formatDateTimeForAPI = (date: string, time: string, allDay: boolean): string => {
    if (allDay) {
      return `${date}T00:00:00`;
    }
    if (date && time) {
      return `${date}T${time}:00`;
    }
    return `${date}T00:00:00`;
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return false;
    }
    if (!formData.start_date) {
      toast.error('Start date is required');
      return false;
    }
    if (formData.email_notification_enabled && !formData.email_recipients.trim()) {
      toast.error('Please enter at least one email recipient');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const eventData: CreateEventData = {
        title: formData.title.trim(),
        type: formData.type,
        priority: formData.priority,
        start_date: formatDateTimeForAPI(formData.start_date, formData.start_time, formData.all_day),
        end_date: formData.end_date ? formatDateTimeForAPI(formData.end_date, formData.end_time, formData.all_day) : undefined,
        all_day: formData.all_day,
        location: formData.location.trim() || undefined,
        description: formData.description.trim() || undefined,
        email_notification_enabled: formData.email_notification_enabled,
        email_recipients: formData.email_notification_enabled 
          ? formData.email_recipients.split(',').map(email => email.trim()).filter(Boolean)
          : undefined,
        reminder_enabled: formData.reminder_enabled,
        reminder_days: formData.reminder_days,
        color: formData.color,
        notes: formData.notes.trim() || undefined,
        assigned_user_ids: formData.assigned_user_ids.length > 0 ? formData.assigned_user_ids : undefined,
      };

      if (editingEvent) {
        await eventsApi.updateEvent(editingEvent.id, eventData);
        toast.success('Event updated successfully');
      } else {
        await eventsApi.createEvent(eventData);
        toast.success('Event created successfully');
      }

      setFormData(defaultFormData);
      setShowForm(false);
      setEditingEvent(null);
      fetchEvents();
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to save event:', error);
      toast.error('Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event: Event) => {
    const startDate = new Date(event.start_date);
    const endDate = event.end_date ? new Date(event.end_date) : null;

    // Handle email_recipients which can be array, string, or null
    let emailRecipientsStr = '';
    if (event.email_recipients) {
      if (Array.isArray(event.email_recipients)) {
        emailRecipientsStr = event.email_recipients.join(', ');
      } else if (typeof event.email_recipients === 'string') {
        try {
          const parsed = JSON.parse(event.email_recipients);
          emailRecipientsStr = Array.isArray(parsed) ? parsed.join(', ') : event.email_recipients;
        } catch {
          emailRecipientsStr = event.email_recipients;
        }
      }
    }

    setFormData({
      title: event.title,
      type: event.type,
      priority: event.priority,
      start_date: startDate.toISOString().split('T')[0],
      start_time: event.all_day ? '' : startDate.toTimeString().slice(0, 5),
      end_date: endDate ? endDate.toISOString().split('T')[0] : '',
      end_time: endDate && !event.all_day ? endDate.toTimeString().slice(0, 5) : '',
      all_day: event.all_day,
      location: event.location || '',
      description: event.description || '',
      email_notification_enabled: event.email_notification_enabled,
      email_recipients: emailRecipientsStr,
      reminder_enabled: event.reminder_enabled,
      reminder_days: event.reminder_days,
      color: event.color,
      notes: event.notes || '',
      assigned_user_ids: event.assignments?.map(a => a.user_id) || [],
    });
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleDelete = async (eventId: number) => {
    if (!confirm('Are you sure you want to delete this event?')) return;

    try {
      await eventsApi.deleteEvent(eventId);
      toast.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event');
    }
  };

  const handleMarkSeen = async (eventId: number) => {
    try {
      await eventsApi.markAsSeen(eventId);
      toast.success('Event marked as seen');
      fetchEvents();
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark event as seen:', error);
      toast.error('Failed to mark event as seen');
    }
  };

  const handleMarkCompleted = async (eventId: number) => {
    try {
      await eventsApi.markAsCompleted(eventId);
      toast.success('Event marked as completed');
      fetchEvents();
    } catch (error) {
      console.error('Failed to mark event as completed:', error);
      toast.error('Failed to mark event as completed');
    }
  };

  const handleCancel = () => {
    setFormData(defaultFormData);
    setEditingEvent(null);
    setShowForm(false);
  };

  const formatDateTime = (dateStr: string, allDay: boolean): string => {
    const date = new Date(dateStr);
    if (allDay) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getPriorityColor = (priority: EventPriority): string => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAssignmentStatusBadge = (status: AssignmentStatus) => {
    switch (status) {
      case 'unseen':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><AlertCircle className="h-3 w-3 mr-1" />Unseen</span>;
      case 'seen':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><Eye className="h-3 w-3 mr-1" />Seen</span>;
      case 'completed':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><Check className="h-3 w-3 mr-1" />Completed</span>;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planner</h1>
          <p className="text-gray-600 mt-1">Manage your events and reminders</p>
        </div>
        <div className="flex items-center space-x-3">
          {unreadCount > 0 && (
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative inline-flex items-center px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors"
            >
              <Bell className="h-5 w-5 mr-2" />
              {unreadCount} New
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Event
          </button>
        </div>
      </div>

      {/* Event Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingEvent ? 'Edit Event' : 'Create New Event'}
            </h2>
            <button
              onClick={handleCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Title */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter event title"
                  required
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="event">Event</option>
                  <option value="reminder">Reminder</option>
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Start Time */}
              {!formData.all_day && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="inline h-4 w-4 mr-1" />
                    Start Time
                  </label>
                  <input
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  End Date
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* End Time */}
              {!formData.all_day && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="inline h-4 w-4 mr-1" />
                    End Time
                  </label>
                  <input
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* All Day Toggle */}
              <div className="md:col-span-2">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="all_day"
                    checked={formData.all_day}
                    onChange={handleCheckboxChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">All day event</span>
                </label>
              </div>

              {/* Location */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter location"
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <AlignLeft className="inline h-4 w-4 mr-1" />
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter event description"
                />
              </div>

              {/* Color Picker */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Palette className="inline h-4 w-4 mr-1" />
                  Color
                </label>
                <div className="flex flex-wrap gap-3">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${
                        formData.color === color.value 
                          ? 'border-gray-900 scale-110' 
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>

              {/* Assign Team Members */}
              {teamMembers.length > 0 && (
                <div className="md:col-span-2 border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Users className="inline h-4 w-4 mr-1" />
                    Assign Team Members
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {teamMembers.map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => handleMemberToggle(member.id)}
                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          formData.assigned_user_ids.includes(member.id)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {member.name}
                        {formData.assigned_user_ids.includes(member.id) && (
                          <Check className="h-4 w-4 ml-1" />
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Selected members will see this event in their Planner and receive a notification.
                  </p>
                </div>
              )}

              {/* Email Notifications */}
              <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-2">
                <label className="flex items-center space-x-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    name="email_notification_enabled"
                    checked={formData.email_notification_enabled}
                    onChange={handleCheckboxChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Send email notification when event is created
                  </span>
                </label>

                {formData.email_notification_enabled && (
                  <div className="ml-7">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Recipients (comma-separated)
                    </label>
                    <input
                      type="text"
                      name="email_recipients"
                      value={formData.email_recipients}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="email1@example.com, email2@example.com"
                    />
                  </div>
                )}
              </div>

              {/* Reminder Settings */}
              <div className="md:col-span-2 border-t border-gray-200 pt-4">
                <label className="flex items-center space-x-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    name="reminder_enabled"
                    checked={formData.reminder_enabled}
                    onChange={handleCheckboxChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    <Bell className="inline h-4 w-4 mr-1" />
                    Enable reminder before event
                  </span>
                </label>

                {formData.reminder_enabled && (
                  <div className="ml-7">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remind me before (days)
                    </label>
                    <input
                      type="number"
                      name="reminder_days"
                      value={formData.reminder_days}
                      onChange={handleInputChange}
                      min={1}
                      max={30}
                      className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional notes"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : editingEvent ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Events List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Your Events</h2>
        </div>
        
        {events.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No events yet. Create your first event!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {events.map((event) => (
              <div key={event.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-4 h-4 rounded-full mt-1 flex-shrink-0"
                      style={{ backgroundColor: event.color }}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-medium text-gray-900">{event.title}</h3>
                        {!event.is_owner && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Assigned by {event.assigned_by_name}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(event.priority)}`}>
                          {event.priority}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {event.type}
                        </span>
                        {event.all_day && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            All day
                          </span>
                        )}
                        {event.reminder_enabled && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Bell className="h-3 w-3 mr-1" />
                            {event.reminder_days} days reminder
                          </span>
                        )}
                        {!event.is_owner && event.assignment_status && (
                          getAssignmentStatusBadge(event.assignment_status)
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        {formatDateTime(event.start_date, event.all_day)}
                        {event.end_date && ` - ${formatDateTime(event.end_date, event.all_day)}`}
                      </p>
                      {event.location && (
                        <p className="text-sm text-gray-500 mt-1">
                          <MapPin className="inline h-3 w-3 mr-1" />
                          {event.location}
                        </p>
                      )}
                      {event.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{event.description}</p>
                      )}
                      
                      {/* Assignment Status for Owners */}
                      {event.is_owner && event.assignments && event.assignments.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs font-medium text-gray-500 mb-2">Assigned Members:</p>
                          <div className="flex flex-wrap gap-2">
                            {event.assignments.map((assignment) => (
                              <div key={assignment.id} className="flex items-center gap-1 text-sm">
                                <span className="text-gray-700">{assignment.user_name}</span>
                                {getAssignmentStatusBadge(assignment.status)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    {event.is_owner ? (
                      <>
                        <button
                          onClick={() => handleEdit(event)}
                          className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(event.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </>
                    ) : (
                      <>
                        {event.assignment_status === 'unseen' && (
                          <button
                            onClick={() => handleMarkSeen(event.id)}
                            className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            Mark as Seen
                          </button>
                        )}
                        {event.assignment_status !== 'completed' && (
                          <button
                            onClick={() => handleMarkCompleted(event.id)}
                            className="px-3 py-1 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          >
                            <Check className="h-4 w-4 inline mr-1" />
                            Complete
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Planner;
